import React from "react";
import { Modal, Tooltip } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone"; // Ensure timezone plugin is extended for .tz() to work reliably
import { useTimeZone, TIMEZONE_OPTIONS, getTimezoneByMode } from "../../contexts/TimeZoneContext";
import { displayFlightDateByTimezone } from "../../utils/timezoneUtils";

dayjs.extend(utc);
dayjs.extend(timezone); // Extend dayjs with timezone plugin

const styles = {
    dateText: {
        // fontFamily: "'JetBrains Mono', 'Fira Mono', 'Menlo', 'Consolas', 'monospace'",
        // fontWeight: 400, // Default antd table font weight is typically 400 or 500
    },
    // Define colors similar to FlightTimeDisplay for consistency
    utcColor: "#1890ff", // Blue for UTC
    beijingColor: "#52c41a", // Green for Beijing
    localColor: "#722ed1", // Purple for Local
    offsetDateColor: "#fa8c16", // Orange color for date offset indication
    missingTzColor: "#ff4d4f", // Red：LT 模式下始发站时区数据缺失，实际按 UTC 显示的告警色
};

const detailTriggerStyle = {
    padding: 0,
    border: 0,
    background: "transparent",
    color: "inherit",
    font: "inherit",
    cursor: "pointer",
};

let activeFlightDateDetailModal = null;

function openFlightDateDetail(title, content) {
    activeFlightDateDetailModal?.destroy?.();
    activeFlightDateDetailModal = Modal.info({
        title,
        content,
        okText: "关闭",
        maskClosable: true,
        afterClose: () => {
            activeFlightDateDetailModal = null;
        },
    });
}

function FlightDateDetailTrigger({ children, content }) {
    return (
        <button
            type="button"
            style={detailTriggerStyle}
            aria-label="查看日期详情"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                openFlightDateDetail("日期详情", content);
            }}
        >
            {children}
        </button>
    );
}

/**
 * Displays the flight date dynamically based on the STD (Standard Time of Departure)
 * and the user-selected timezone. The text color changes to orange if the displayed date
 * is different from the original UTC date of the STD due to timezone conversion.
 *
 * @param {object} props
 * @param {string} props.etdOrStd - The Standard Time of Departure in UTC (ISO string format).
 * @param {string} [props.departureAirportCode] - The IATA code of the departure airport, used for 'Local' timezone mode.
 * @param {string} [props.targetFormat="D-MMM"] - The format for the displayed date string.
 * @param {object} [props.style] - Custom styles for the component's root span.
 * @param {string} [props.originalFlightDateForFallback] - Optional: Original flight_date from record for fallback if STD is missing.
 */
const FlightDateDisplay = ({ etdOrStd, departureAirportCode, targetFormat = "D-MMM", style, originalFlightDateForFallback, useClickDetail = false, disableDetail = false }) => {
    const { timeZone: timeZoneMode, airportsMap } = useTimeZone();

    const tooltipTitle = etdOrStd ? `${dayjs.utc(etdOrStd).format("YYYY-MM-DD HH:mm")} UTC` : "STD not available";

    // LOCAL(LT) 模式下，若始发站时区数据缺失（机场接口 403 / 尚未加载 / 机场不在返回列表里），
    // getTimezoneByMode 会静默降级为 UTC，导致“看着是 LT、其实按 UTC 显示”。
    // 这里显式标红并加问号 + 提示，避免被误当成始发站当地日期。
    const localTimezoneMissing =
        timeZoneMode === TIMEZONE_OPTIONS.LOCAL && !(departureAirportCode && airportsMap && airportsMap[departureAirportCode] && airportsMap[departureAirportCode].timezone);

    if (localTimezoneMissing) {
        const baseInstant = etdOrStd || originalFlightDateForFallback;
        const utcDateText = baseInstant
            ? dayjs
                  .utc(baseInstant)
                  .format(targetFormat)
                  .replace(/[^\d\-A-Za-z]/g, "")
            : "--";
        const missingTzTooltip = departureAirportCode
            ? `始发站 ${departureAirportCode} 的时区数据未加载（可能无权限或尚未加载完成），无法换算为当地时间，当前按 UTC 显示`
            : "缺少始发站机场代码，无法换算为当地时间，当前按 UTC 显示";
        const display = (
            <span style={{ ...styles.dateText, color: styles.missingTzColor, ...style }}>
                {utcDateText}
                <sup style={{ color: styles.missingTzColor, fontWeight: 700, marginLeft: 1 }}>?</sup>
            </span>
        );
        if (disableDetail) {
            return display;
        }
        return useClickDetail ? <FlightDateDetailTrigger content={missingTzTooltip}>{display}</FlightDateDetailTrigger> : <Tooltip title={missingTzTooltip}>{display}</Tooltip>;
    }

    if (!etdOrStd) {
        // Fallback logic for missing STD
        let fallbackTextColor;
        if (originalFlightDateForFallback && timeZoneMode !== TIMEZONE_OPTIONS.UTC) {
            const utcDatePart = dayjs.utc(originalFlightDateForFallback).format("YYYY-MM-DD");
            const targetTimezoneForFallback = getTimezoneByMode(timeZoneMode, departureAirportCode, airportsMap);
            const targetDatePart = dayjs.utc(originalFlightDateForFallback).tz(targetTimezoneForFallback).format("YYYY-MM-DD");
            if (utcDatePart !== targetDatePart) {
                fallbackTextColor = styles.offsetDateColor;
            }
        }
        const fallbackDateText = originalFlightDateForFallback
            ? dayjs
                  .utc(originalFlightDateForFallback)
                  .format(targetFormat)
                  .replace(/[^\d\-A-Za-z]/g, "")
            : "--";
        const fallbackTooltip = originalFlightDateForFallback ? `${dayjs.utc(originalFlightDateForFallback).format("YYYY-MM-DD HH:mm")} UTC (from flight_date)` : tooltipTitle;
        const display = <span style={{ ...styles.dateText, color: fallbackTextColor, ...style }}>{fallbackDateText}</span>;
        if (disableDetail) {
            return display;
        }
        return useClickDetail ? <FlightDateDetailTrigger content={fallbackTooltip}>{display}</FlightDateDetailTrigger> : <Tooltip title={fallbackTooltip}>{display}</Tooltip>;
    }

    try {
        const targetTimezone = getTimezoneByMode(timeZoneMode, departureAirportCode, airportsMap);
        const dateToShow = displayFlightDateByTimezone(etdOrStd, targetTimezone, targetFormat);

        let textColor; // Default is undefined, so it inherits or uses antd default
        // Determine if the displayed date is different from the UTC date of STD
        if (timeZoneMode !== TIMEZONE_OPTIONS.UTC) {
            // Only check for offset if not in UTC mode
            const stdUtcDatePart = dayjs.utc(etdOrStd).format("YYYY-MM-DD");
            const stdTargetDatePart = dayjs.utc(etdOrStd).tz(targetTimezone).format("YYYY-MM-DD");
            if (stdUtcDatePart !== stdTargetDatePart) {
                textColor = styles.offsetDateColor;
            }
        }

        const display = <span style={{ ...styles.dateText, color: textColor, ...style }}>{dateToShow.replace(/[^\d\-A-Za-z]/g, "")}</span>;
        if (disableDetail) {
            return display;
        }
        return useClickDetail ? <FlightDateDetailTrigger content={tooltipTitle}>{display}</FlightDateDetailTrigger> : <Tooltip title={tooltipTitle}>{display}</Tooltip>;
    } catch (error) {
        console.error("Error displaying flight date:", error, { std: etdOrStd, timeZoneMode, departureAirportCode });
        const fallbackDate = dayjs
            .utc(etdOrStd)
            .format(targetFormat)
            .replace(/[^\d\-A-Za-z]/g, "");
        const fallbackTooltip = `Error - Original STD: ${tooltipTitle}`;
        const display = <span style={{ ...styles.dateText, color: "red", ...style }}>{fallbackDate || "Err"}</span>;
        if (disableDetail) {
            return display;
        }
        return useClickDetail ? <FlightDateDetailTrigger content={fallbackTooltip}>{display}</FlightDateDetailTrigger> : <Tooltip title={fallbackTooltip}>{display}</Tooltip>;
    }
};

export default FlightDateDisplay;
